export type ISignal<T extends (...args: any[]) => void> = {
    add(listener: T, context?: any): void;
    once(listener: T, context?: any): void;
    remove(listener?: T | any, context?: any): void;
} & T;

interface ISignalInternal<T> {
    _calling?: boolean;
    _subsequent: any[][];
    _listeners: Array<{
        listener: T | undefined;
        context: any;
        once?: boolean;
        deleted?: boolean
    }>;
}

const SignalExtensions = {

    add<T>(this: ISignalInternal<T>, listener: T, context?: any): void {
        if (listener) {
            if (!this._listeners) {
                this._listeners = [{listener, context}];
            } else {
                this._listeners.push({listener, context});
            }
        }
    },

    once<T>(this: ISignalInternal<T>, listener: T, context?: any): void {
        if (listener) {
            if (!this._listeners) {
                this._listeners = [{listener, context, once: true}];
            } else {
                this._listeners.push({listener, context, once: true});
            }
        }
    },

    remove<T>(this: ISignalInternal<T>, listener: T | any, context?: any): void {
        if (this._listeners) {
            for (const it of this._listeners) {
                if ((listener && context && it.listener === listener && it.context === context) ||
                    (listener && (it.listener === listener || it.context === listener)) ||
                    (arguments.length === 0)) {
                    it.listener = undefined;
                    it.context = null;
                    it.deleted = true;
                }
            }
        }
    },
};

export function Signal<T extends (...args: any[]) => void>(): ISignal<T> & T {
    function s() {
        const si: ISignalInternal<T> = s as any;
        if (si._calling) {
            if (si._subsequent === null) {
                si._subsequent = [];
            }
            si._subsequent.push(Array.prototype.slice.call(arguments));
        } else if (si._listeners) {
            si._calling = true;
            let anyDeleted = false;
            const listeners = si._listeners;
            const n = listeners.length; // don't call listeners added during this call
            for (let i = 0; i < n; i++) {
                const listener = listeners[i];
                if (listener && !listener.deleted) { // check if the listener is deleted in 'remove' function
                    if (arguments.length === 0) {
                        listener.listener!.call(listener.context);
                    } else {
                        listener.listener!.apply(listener.context, Array.prototype.slice.call(arguments));
                    }
                    if (listener.once) {
                        listener.listener = undefined;
                        listener.context = null;
                        listener.deleted = true;
                    }
                }
                if (listener.deleted) {
                    anyDeleted = true;
                }
            }
            if (anyDeleted && si._listeners) { // delete listeners called once
                si._listeners = si._listeners.filter((it) => !it.deleted);
            }
            si._calling = false;

            if (si._subsequent != null && si._subsequent.length > 0) {
                const args = si._subsequent.shift();
                if (args!.length === 0) {
                    s.call(null);
                } else {
                    s.apply(null, args as []);
                }
            }
        }
    }
    s.add = SignalExtensions.add;
    s.once = SignalExtensions.once;
    s.remove = SignalExtensions.remove;
    return s as any;
}

export function Once<T extends (...args: any[]) => void>(func: T, context?: any): T {
    return function F() {
        if (func) {
            const f = func;
            const c = context;
            (func as any) = undefined;
            context = undefined;
            if (arguments.length === 0) {
                f.call(c);
            } else {
                f.apply(c, Array.prototype.slice.call(arguments));
            }
        }
    } as any;
}

export function Times<T extends (...args: any[]) => void>(times: number, func: T, context?: any): T {
    return function F() {
        times--;
        if (times <= 0 && func) {
            const f = func;
            const c = context;
            (func as any) = undefined;
            context = undefined;
            if (arguments.length === 0) {
                f.call(c);
            } else {
                f.apply(c, Array.prototype.slice.call(arguments));
            }
        }
    } as any;
}
